import { Array, Data, Match, Option } from 'effect';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';

import type { FacetSet } from '../../capability/productListing/facetSet';

export type ListingFacets = Data.TaggedEnum<{
  Pending: { readonly retained: Option.Option<FacetSet> };
  Available: { readonly value: FacetSet };
  Unavailable: { readonly retained?: Option.Option<FacetSet> };
}>;

export const ListingFacets = Data.taggedEnum<ListingFacets>();

/** Returns visible choices without requiring callers to infer lifecycle state. */
export const listingFacetSet = (state: ListingFacets): Option.Option<FacetSet> =>
  Match.value(state).pipe(
    Match.tag('Pending', ({ retained }) => retained),
    Match.tag('Available', ({ value }) => Option.some(value)),
    Match.tag('Unavailable', ({ retained }) =>
      Option.getOrElse(Option.fromUndefinedOr(retained), () => Option.none<FacetSet>())
    ),
    Match.exhaustive
  );

/** Retains choices and range bounds while target counts remain unknown. */
export const resolveListingFacets = <E>(
  result: AsyncResult.AsyncResult<FacetSet, E>,
  previous: Option.Option<ListingFacets>
): ListingFacets =>
  AsyncResult.match(result, {
    onInitial: () =>
      ListingFacets.Pending({
        retained: previous.pipe(
          Option.flatMap(listingFacetSet),
          Option.map((set) => ({
            ...set,
            facets: Array.map(set.facets, (facet) =>
              Match.value(facet).pipe(
                Match.tag('Terms', (terms) => ({
                  ...terms,
                  options: Array.map(terms.options, (option) => ({ ...option, count: null })),
                })),
                Match.tag('Range', (range) => range),
                Match.exhaustive
              )
            ),
          }))
        ),
      }),
    onFailure: () =>
      Option.match(AsyncResult.value(result), {
        onNone: () => ListingFacets.Unavailable({}),
        onSome: (value) => ListingFacets.Available({ value }),
      }),
    onSuccess: ({ value }) => ListingFacets.Available({ value }),
  });
